import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { Plus, X, Search, ListChecks, ListPlus } from 'lucide-react';

const PAGE_SIZE = 10;

export default function ChecklistPage() {
  const navigate = useNavigate();
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [name, setName] = useState('');

  const fetchChecklists = () => {
    setLoading(true);
    api.get('/checklists').then((r) => setChecklists(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchChecklists(); }, []);

  const q = search.trim().toLowerCase();
  const filtered = !q ? checklists : checklists.filter((c) => c.name?.toLowerCase().includes(q));
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [search]);

  const createChecklist = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Enter a checklist name'); return; }
    try {
      await api.post('/checklists', { name });
      toast.success('Checklist created');
      setShowModal(false);
      setName('');
      fetchChecklists();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2>Create Checklist</h2>
          <p className="text-muted">Create and manage checklists</p>
        </div>
        <div className="header-actions">
          <div className="search-bar">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search checklists..."
            />
          </div>
          <button className="btn btn-primary" onClick={() => { setName(''); setShowModal(true); }}>
            <Plus size={16} /> Create Checklist
          </button>
        </div>
      </div>

      <div className="table-container table-scroll">
        <table className="table">
          <thead>
            <tr>
              <th>Checklist Name</th>
              <th>No. of Tasks</th>
              <th>Items</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((c) => (
              <tr key={c._id}>
                <td><strong>{c.name}</strong></td>
                <td>
                  <span className="badge"><ListChecks size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{c.itemCount || 0}</span>
                </td>
                <td>
                  <button className="btn btn-sm btn-outline" onClick={() => navigate(`/checklist/${c._id}`)}>
                    <ListPlus size={13} /> Add Items
                  </button>
                </td>
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={3} className="empty-state">
                {checklists.length === 0 ? 'No checklists yet. Create one to get started.' : 'No checklists match your search.'}
              </td></tr>
            )}
            {loading && <tr><td colSpan={3} className="empty-state">Loading...</td></tr>}
          </tbody>
        </table>
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="pagination">
          <span className="pagination-info">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="pagination-controls">
            <button className="btn btn-sm btn-outline" onClick={() => setPage(1)} disabled={page === 1}>«</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p - 1)} disabled={page === 1}>‹ Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
              .map((p, idx) => p === '...'
                ? <span key={`e${idx}`} className="pagination-ellipsis">…</span>
                : <button key={p} className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-outline'}`} onClick={() => setPage(p)}>{p}</button>)}
            <button className="btn btn-sm btn-outline" onClick={() => setPage((p) => p + 1)} disabled={page === totalPages}>Next ›</button>
            <button className="btn btn-sm btn-outline" onClick={() => setPage(totalPages)} disabled={page === totalPages}>»</button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Create Checklist</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => setShowModal(false)} title="Close">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={createChecklist}>
              <div className="form-group">
                <label>Checklist Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Kitchen Opening Checklist"
                  autoFocus
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
